import {
  BlobReader,
  BlobWriter,
  ZipReader,
  type FileEntry,
} from "@zip.js/zip.js";
import exifr from "exifr";
import { createSHA256 } from "hash-wasm";

import { isJpegEntry, type OrthoImageSource } from "./orthoUpload";

const MARKER_PREFIX = 0xff;
const MARKER_SOI = 0xd8;
const MARKER_EOI = 0xd9;
const MARKER_SOS = 0xda;

/**
 * Walks JPEG segment headers (`FF <marker> <2-byte length> <payload>`) and skips
 * each payload by its declared length. EXIF and XMP live in APPn segments ahead of
 * the first SOS marker, where compressed image data begins.
 *
 * Resumable: pass the bytes read so far and the offset returned last time.
 * Returns the offset of the next unparsed segment, or `null` once the header is
 * complete (SOS/EOI reached) or the data isn't a JPEG.
 */
const advanceJpegSegments = (
  bytes: Uint8Array,
  startOffset: number,
): number | null => {
  let offset = startOffset;
  if (offset === 0) {
    if (bytes.length < 2) {
      return 0;
    }
    if (bytes[0] !== MARKER_PREFIX || bytes[1] !== MARKER_SOI) {
      return null;
    }
    offset = 2;
  }

  while (offset < bytes.length) {
    if (bytes[offset] !== MARKER_PREFIX) {
      return null;
    }
    // Markers may be padded with extra 0xFF fill bytes.
    let markerAt = offset + 1;
    while (bytes[markerAt] === MARKER_PREFIX) {
      markerAt++;
    }
    if (markerAt >= bytes.length) {
      return offset;
    }

    const marker = bytes[markerAt];
    if (marker === MARKER_SOS || marker === MARKER_EOI || marker === 0x00) {
      return null;
    }
    // TEM and RSTn markers stand alone, with no length or payload.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      offset = markerAt + 1;
      continue;
    }

    if (markerAt + 2 >= bytes.length) {
      return offset;
    }
    // Length counts its own two bytes plus the payload.
    const length = (bytes[markerAt + 1] << 8) | bytes[markerAt + 2];
    if (length < 2) {
      return null;
    }
    offset = markerAt + 1 + length;
  }
  return offset;
};

const concatBytes = (a: Uint8Array, b: Uint8Array): Uint8Array<ArrayBuffer> => {
  const merged = new Uint8Array(a.length + b.length);
  merged.set(a);
  merged.set(b, a.length);
  return merged;
};

/** Entries are small head reads, so skip the cost (and CSP exposure) of spinning up workers. */
const ZIP_OPTIONS = { useWebWorkers: false };

/** One JPEG inside a zip, uploaded under the same folder layout as if the zip had been extracted. */
export interface OrthoZipImageEntry {
  /** Directory it uploads under; starts with `OrthoZipArchive.folderName`. */
  directoryPath: string;
  /** Filename without directories. */
  name: string;
  /** Uncompressed size in bytes. */
  size: number;
  /** Streams the decompressed bytes into `writable` without holding them. */
  streamTo: (writable: WritableStream<Uint8Array>) => Promise<void>;
  /** Decompresses the whole image into memory. */
  toBlob: () => Promise<Blob>;
  /** Small JPEG thumbnail embedded in the image's EXIF, read from the header */
  toThumbnailBlob: () => Promise<Blob | null>;
}

export interface OrthoZipArchive {
  /** The JPEGs in the zip, in archive order. */
  entries: OrthoZipImageEntry[];
  /** Top-level folder the upload is grouped under. */
  folderName: string;
  imageCount: number;
  close: () => Promise<void>;
  /** Yields each JPEG entry's head one at a time, so only one is held in memory. */
  images: () => AsyncGenerator<OrthoImageSource>;
}

/**
 * Decompresses an entry only up to the end of its JPEG header (the first SOS
 * marker), aborting the rest, so metadata is read without extracting the image.
 */
const readEntryHead = async (entry: FileEntry): Promise<Blob> => {
  let head = new Uint8Array(0);
  let nextSegment: number | null = 0;
  const abortController = new AbortController();

  const writable = new WritableStream<Uint8Array>({
    write: (chunk) => {
      if (nextSegment === null) {
        return;
      }
      head = concatBytes(head, chunk);
      nextSegment = advanceJpegSegments(head, nextSegment);
      if (nextSegment === null) {
        abortController.abort();
      }
    },
  });

  try {
    await entry.getData(writable, {
      ...ZIP_OPTIONS,
      signal: abortController.signal,
    });
  } catch (error) {
    // The abort above is how we stop early; anything else is a real failure.
    if (nextSegment !== null) {
      throw error;
    }
  }

  return new Blob([head]);
};

const readEntryThumbnail = async (entry: FileEntry): Promise<Blob | null> => {
  const thumbnail = await exifr.thumbnail(await readEntryHead(entry));
  return thumbnail == null
    ? null
    : new Blob([new Uint8Array(thumbnail)], { type: "image/jpeg" });
};

const splitPath = (path: string): string[] =>
  path.replaceAll("\\", "/").split("/").filter(Boolean);

/**
 * A zip of a folder keeps that folder as its top level, so its paths already
 * match a folder upload. Otherwise the zip's own name stands in for the folder.
 */
const getFolderLayout = (
  entryPaths: string[][],
  zipName: string,
): { folderName: string; prefix: string[] } => {
  const [firstSegment] = entryPaths[0] ?? [];
  const sharesRootFolder =
    firstSegment != null &&
    entryPaths.every(
      (segments) => segments.length > 1 && segments[0] === firstSegment,
    );
  if (sharesRootFolder) {
    return { folderName: firstSegment, prefix: [] };
  }
  const folderName = zipName.replace(/\.zip$/i, "");
  return { folderName, prefix: [folderName] };
};

/**
 * SHA-256 hex digests of each entry's decompressed bytes, in entry order. Matches
 * `hashFilesSha256` on the extracted files, and streams so no entry is held in
 * memory. Entries are read one at a time and share a single WASM hasher.
 */
export const hashOrthoZipEntries = async (
  entries: OrthoZipImageEntry[],
): Promise<string[]> => {
  const hasher = await createSHA256();
  const digests: string[] = [];
  for (const entry of entries) {
    hasher.init();
    await entry.streamTo(
      new WritableStream<Uint8Array>({
        write: (chunk) => {
          hasher.update(chunk);
        },
      }),
    );
    digests.push(hasher.digest("hex"));
  }
  return digests;
};

/** Reads the zip's central directory (no image data) and lists its JPEG entries. */
export const openOrthoZip = async (file: File): Promise<OrthoZipArchive> => {
  const reader = new ZipReader(new BlobReader(file), ZIP_OPTIONS);

  try {
    const zipEntries = (await reader.getEntries()).filter(
      (entry): entry is FileEntry =>
        !entry.directory && isJpegEntry(entry.filename),
    );

    if (zipEntries.some((entry) => entry.encrypted)) {
      throw new Error("Password-protected zip files are not supported");
    }

    const entryPaths = zipEntries.map((entry) => splitPath(entry.filename));
    const { folderName, prefix } = getFolderLayout(entryPaths, file.name);

    const entries = zipEntries.map((entry, index): OrthoZipImageEntry => {
      const segments = entryPaths[index];
      return {
        directoryPath: [...prefix, ...segments.slice(0, -1)].join("/"),
        name: segments[segments.length - 1],
        size: entry.uncompressedSize,
        streamTo: async (writable) => {
          await entry.getData(writable, ZIP_OPTIONS);
        },
        toBlob: () => entry.getData(new BlobWriter("image/jpeg"), ZIP_OPTIONS),
        toThumbnailBlob: () => readEntryThumbnail(entry),
      };
    });

    return {
      close: () => reader.close(),
      entries,
      folderName,
      imageCount: entries.length,
      images: async function* () {
        for (const [index, entry] of zipEntries.entries()) {
          yield {
            exifData: await readEntryHead(entry),
            name: entries[index].name,
            size: entry.uncompressedSize,
          };
        }
      },
    };
  } catch (error) {
    await reader.close();
    throw error;
  }
};
